import { HttpEventType } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { GroupBy } from 'src/app/core/_enums/groupby';
import { SiteMapUrl } from 'src/app/core/_enums/sitemap.url';
import { SnackbarTypes } from 'src/app/core/_enums/snackbar.types';
import { Atelier } from 'src/app/core/_models/atelier';
import { Ceu } from 'src/app/core/_models/ceu';
import { ProjectType, compare } from 'src/app/core/_models/projectType';
import { Version } from 'src/app/core/_models/version';
import { AuthService } from 'src/app/core/_services/auth.service';
import { CeuService } from 'src/app/core/_services/ceu.service';
import { EnginAtelierService } from 'src/app/core/_services/engin.atelier.service';
import { ProjectTypeService } from 'src/app/core/_services/projectTypes.service';
import { SnackbarService } from 'src/app/core/_services/snackbar.service';
import { SpinnerOverlayService } from 'src/app/core/_services/spinner.overlay.service';
import { UploadService } from 'src/app/core/_services/upload.service';
import { VersionService } from 'src/app/core/_services/versions.service';
import { ResourceListRefreshService } from 'src/app/core/_services/resource.list.refresh.service';
import { UtilsService } from 'src/app/core/_utils/utils.service';
import { validateProjectTypeRequired } from 'src/app/core/_utils/validation';
import { StoreSelect, UpdateResourceAction } from 'src/app/store/actions/resource.actions';
import { AppState } from 'src/app/store/app.state';
import { GlobalConstants } from 'src/constants/global.constants';
import { environment } from 'src/environments/environment';
import { Energie } from '../../core/_models/energie';
import { EnvironmentalData } from '../../core/_models/environmentalData';
import { EnergieService } from '../../core/_services/energie.service';
import { EnvironmentalDataService } from '../../core/_services/environmentalDataService';
import { DialogDownloadCalculCeuEnginsComponent } from '../shared/dialog-download-calcul-ceu-engins/dialog-download-calcul-ceu-engins.component';
import { DialogDuplicateCodeComponent } from '../shared/dialog-duplicate-code/dialog-duplicate-code.component';
import { DialogOverviewComponent } from '../shared/dialog-overview/dialog-overview.component';

@Component({
  selector: 'app-engin',
  templateUrl: './engin.component.html',
  styleUrls: ['./engin.component.scss']
})
export class EnginComponent implements OnInit {
  EMPTY_GUID = GlobalConstants.EMPTY_GUID;
  @Input() groupBy!: GroupBy;
  @ViewChild('resourceSection', { static: false }) resourceSection!: ElementRef;

  currentItem: Atelier = {};
  form!: FormGroup;
  families!: any[];
  perimeterId!: string;
  favoriteId?: string;
  wsAppPath: string;
  projectTypes!: ProjectType[];

  dbVersions: Version[] = [];
  energies: Energie[] = [];
  currentEnvData?: EnvironmentalData;
  energyUnavailable: boolean = false;
  isLoadingEnvData: boolean = false;
  formSubmitted?: boolean;

  fileToUploadOnSave: File | undefined;
  fileToUploadName: string | undefined;
  calculFileToUploadOnSave: File | undefined;
  calculFileToUploadName: string | undefined;

  constructor(
    private service: EnginAtelierService,
    private activeRoute: ActivatedRoute,
    private formBuilder: FormBuilder,
    private router: Router,
    private snackbarService: SnackbarService,
    private store: Store<AppState>,
    public dialog: MatDialog,
    private uploadService: UploadService,
    private utilsService: UtilsService,
    private ceuService: CeuService,
    private versionService: VersionService,
    private authService: AuthService,
    private projectTypeService: ProjectTypeService,
    private translate: TranslateService,
    private energieService: EnergieService,
    private environmentalDataService: EnvironmentalDataService,
    private spinnerService: SpinnerOverlayService,
    private resourceListRefreshService: ResourceListRefreshService
  ) {
    this.wsAppPath = environment.baseUrl + '/Documents/Justification/';
  }

  ngOnInit(): void {
    this.perimeterId = this.authService.roleSubject.value.perimeterId;
    this.activeRoute.params.subscribe((params) => {
      this.form = this.formBuilder.group({
        code: ['', Validators.required],
        displayName: ['', Validators.required],
        owner: [{ value: '', disabled: true }],
        creator: [{ value: '', disabled: true }],
        detailedResource: [{ value: false, disabled: true }],
        familyId: ['', Validators.required],
        description: [''],
        ceuCarburant: this.formBuilder.group({
          ceuCarburantEn: ['', Validators.required],
          ceuCarburantCo: ['', Validators.required]
        }),
        ceuTransfert: this.formBuilder.group({
          ceuTransfertEn: ['', Validators.required],
          ceuTransfertCo: ['', Validators.required]
        }),
        ceuFabrication: this.formBuilder.group({
          ceuFabricationEn: ['', Validators.required],
          ceuFabricationCo: ['', Validators.required]
        }),
        ceuEntretien: this.formBuilder.group({
          ceuEntretienEn: ['', Validators.required],
          ceuEntretienCo: ['', Validators.required]
        }),
        ceu: this.formBuilder.group({
          en: ['', Validators.required],
          co: ['', Validators.required]
        }),
        ceuHeure: this.formBuilder.group({
          en: ['', Validators.required],
          co: ['', Validators.required]
        }),
        roadProject: [],
        earthworkProject: [],
        projectType: [[], validateProjectTypeRequired('validation_project_type_required')],
        versionId: [''],
        energyId: ['']
      });
      const id = params['id'];
      const resourceId = id === 'new' ? undefined : id;

      this.versionService.getAll().subscribe((versions) => {
        this.dbVersions = versions;
        this.loadLists(resourceId).finally(() => {
          if (resourceId) {
            this.loadEngin(resourceId);
          } else {
            let obj: Atelier = { detailedResource: false };
            this.initForm(this.form, obj);
          }
        });
      });
    });
  }

  async loadEngin(resourceId: string) {
    this.service.get(resourceId).subscribe(
      (item) => {
        this.initForm(this.form, item);
      },
      (error) => {
        this.snackbarService.open(SnackbarTypes.error);
        this.router.navigate([SiteMapUrl.MenuEnginAtelier]);
      }
    );
  }

  async loadLists(resourceId?: string) {
    this.families = !history.state?.families ? await this.service.getFamilies().toPromise() : history.state.families;
    this.projectTypes = !history.state?.projectTypes ? await this.projectTypeService.getAll().toPromise() : history.state.projectTypes;
    this.energies = await this.energieService.getAllForVehicle().toPromise();
  }

  initForm(form: FormGroup, currentItem: Atelier) {
    this.currentItem = currentItem;
    if (this.currentItem && this.currentItem.resourceId) {
      this.utilsService.getFavoriteIdForResource(this.currentItem.resourceId).then((res) => {
        this.favoriteId = res ? res.userFavoriteId : '';
      });
    }

    form.reset();

    this.form.patchValue({
      versionId: this.dbVersions[0].versionId,
      energyId: this.energies?.[0]?.resourceId
    });

    if (this.currentItem && this.currentItem.resourceId && !this.currentItem.engins) {
      form.patchValue(currentItem);
      const versionForCheck = this.formCrls.versionId.value ?? this.dbVersions[0].versionId;
      const eId = this.formCrls.energyId.value ?? '';
      this.loadCeuAndEnvData(eId, versionForCheck, () => this.onChangeValuesCeu());
    } else {
      form.patchValue(currentItem);
      this.onChangeValuesCeu();
      const defaultEnergyId = this.formCrls.energyId.value;
      const defaultVersionId = this.formCrls.versionId.value;
      if (defaultEnergyId) {
        this.loadEnvData(defaultEnergyId, defaultVersionId);
      }
    }

    this.formCrls?.ceu?.valueChanges.subscribe(() => {
      this.onChangeValuesCeu();
    });
    this.formCrls?.ceuCarburant?.get('ceuCarburantEn')?.valueChanges.subscribe(() => {
      this.onChangeValuesCarburantCeu();
    });
    this.formCrls?.ceuEntretien?.valueChanges.subscribe(() => {
      this.onChangeValuesOtherCeu();
    });
    this.formCrls?.ceuTransfert?.valueChanges.subscribe(() => {
      this.onChangeValuesOtherCeu();
    });
    this.formCrls?.ceuFabrication?.valueChanges.subscribe(() => {
      this.onChangeValuesOtherCeu();
    });
  }

  onChangeValuesCeu() {
    this.formCrls?.ceuHeure?.get('en')?.setValue(this.calculCeuHeure(this.formCrls?.ceu?.get('en')?.value));
    this.formCrls?.ceuHeure?.get('co')?.setValue(this.calculCeuHeure(this.formCrls?.ceu?.get('co')?.value));
  }

  get isAutreEnergie(): boolean {
    const energyId = this.formCrls?.energyId?.value;
    return !energyId || energyId === '' || energyId === GlobalConstants.EMPTY_GUID;
  }

  get isNewEngine(): boolean {
    return !this.currentItem?.resourceId;
  }

  onChangeValuesCarburantCeu() {
    if (this.isAutreEnergie) {
      this.onChangeValuesOtherCeu();
      return;
    }
    if (this.currentEnvData && this.formCrls?.ceuCarburant?.get('ceuCarburantEn')?.value !== undefined) {
      this.formCrls?.ceuCarburant?.get('ceuCarburantCo')?.setValue(this.formCrls?.ceuCarburant?.get('ceuCarburantEn')?.value * (this.currentEnvData?.environmentalFactor ?? 1));
      this.onChangeValuesOtherCeu();
    }
  }

  onChangeValuesOtherCeu() {
    if (
      this.formCrls?.ceuCarburant?.get('ceuCarburantCo')?.value !== undefined &&
      this.formCrls?.ceuTransfert?.get('ceuTransfertCo')?.value !== undefined &&
      this.formCrls?.ceuFabrication?.get('ceuFabricationCo')?.value !== undefined &&
      this.formCrls?.ceuEntretien?.get('ceuEntretienCo')?.value !== undefined &&
      this.formCrls?.ceuCarburant?.get('ceuCarburantEn')?.value !== undefined &&
      this.formCrls?.ceuTransfert?.get('ceuTransfertEn')?.value !== undefined &&
      this.formCrls?.ceuFabrication?.get('ceuFabricationEn')?.value !== undefined &&
      this.formCrls?.ceuEntretien?.get('ceuEntretienEn')?.value !== undefined
    ) {
      this.formCrls?.ceu
        ?.get('co')
        ?.setValue(
          (this.formCrls?.ceuCarburant?.get('ceuCarburantCo')?.value ?? 0) +
            (this.formCrls?.ceuTransfert?.get('ceuTransfertCo')?.value ?? 0) +
            (this.formCrls?.ceuFabrication?.get('ceuFabricationCo')?.value ?? 0) +
            (this.formCrls?.ceuEntretien?.get('ceuEntretienCo')?.value ?? 0)
        );
      this.formCrls?.ceu
        ?.get('en')
        ?.setValue(
          (this.formCrls?.ceuCarburant?.get('ceuCarburantEn')?.value ?? 0) +
            (this.formCrls?.ceuTransfert?.get('ceuTransfertEn')?.value ?? 0) +
            (this.formCrls?.ceuFabrication?.get('ceuFabricationEn')?.value ?? 0) +
            (this.formCrls?.ceuEntretien?.get('ceuEntretienEn')?.value ?? 0)
        );
      this.onChangeValuesCeu();
    }
  }

  onVersionChange(value: string) {
    this.energyUnavailable = false;
    this.loadCeuAndEnvData(this.formCrls.energyId.value, value);
  }

  onEnergyChange(value: string) {
    this.currentEnvData = undefined;
    this.energyUnavailable = false;
    if (!this.currentItem?.resourceId) {
      this.loadEnvData(value, this.formCrls.versionId.value);
      return;
    }
    this.loadCeuAndEnvData(value, this.formCrls.versionId.value);
  }

  private loadEnvData(eId: string, version: string | undefined, after: () => void = () => this.onChangeValuesCarburantCeu()): void {
    if (!eId || eId === GlobalConstants.EMPTY_GUID) {
      this.currentEnvData = undefined;
      this.isLoadingEnvData = false;
      after();
      return;
    }
    this.isLoadingEnvData = true;
    this.environmentalDataService
      .getVehicleEnvironmentalData(eId, version)
      .pipe(
        catchError(() => {
          this.currentEnvData = undefined;
          this.energyUnavailable = false;
          return of(undefined);
        })
      )
      .subscribe((envData) => {
        this.applyEnvData(envData);
        this.isLoadingEnvData = false;
        after();
      });
  }

  private loadCeuAndEnvData(eId: string, version: string | undefined, afterCeuLoaded?: () => void): void {
    this.isLoadingEnvData = true;
    const versionForCeu = this.currentItem.ruade ? version : undefined;
    this.ceuService.getCeu(this.currentItem.resourceId!, versionForCeu, eId).subscribe((ceu: Ceu) => {
      this.loadEnvData(eId, version, () => {
        if (ceu) this.setFormCeu(ceu);
        afterCeuLoaded?.();
      });
    });
  }

  private applyEnvData(envData: EnvironmentalData | undefined) {
    if (envData && envData.environmentalFactor === undefined) {
      this.currentEnvData = undefined;
      this.energyUnavailable = true;
      return;
    }
    if (envData) this.currentEnvData = envData;
    this.energyUnavailable = false;
  }

  setFormCeu(ceu: any) {
    let isEmpty: boolean = Object.keys(ceu)?.length == 0;
    if (isEmpty) {
      this.snackbarService.open(SnackbarTypes.ResourceNotAvailableForversion);
    }

    if (!this.isAutreEnergie && this.currentEnvData && ceu.ceuEngin?.ca?.energie !== undefined) {
      ceu.ceuEngin.ca.cO2 = ceu.ceuEngin.ca.energie * (this.currentEnvData?.environmentalFactor ?? 1);
      ceu.co = (ceu.ceuEngin.ca.cO2 ?? 0) + (ceu.ceuEngin.tr.cO2 ?? 0) + (ceu.ceuEngin.fa.cO2 ?? 0) + (ceu.ceuEngin.en.cO2 ?? 0);
      ceu.en = (ceu.ceuEngin.ca.energie ?? 0) + (ceu.ceuEngin.tr.energie ?? 0) + (ceu.ceuEngin.fa.energie ?? 0) + (ceu.ceuEngin.en.energie ?? 0);
    }

    this.form.patchValue({
      ceu: {
        en: isEmpty ? '' : ceu.en,
        co: isEmpty ? '' : ceu.co
      },
      ceuCarburant: {
        ceuCarburantEn: ceu.ceuEngin?.ca?.energie,
        ceuCarburantCo: ceu.ceuEngin?.ca?.cO2
      },
      ceuTransfert: {
        ceuTransfertEn: ceu.ceuEngin?.tr?.energie,
        ceuTransfertCo: ceu.ceuEngin?.tr?.cO2
      },
      ceuFabrication: {
        ceuFabricationEn: ceu.ceuEngin?.fa?.energie,
        ceuFabricationCo: ceu.ceuEngin?.fa?.cO2
      },
      ceuEntretien: {
        ceuEntretienEn: ceu.ceuEngin?.en?.energie,
        ceuEntretienCo: ceu.ceuEngin?.en?.cO2
      }
    });
  }

  isFormDisable() {
    var isInCreation = !this.currentItem || !this.currentItem.resourceId;
    return !isInCreation && !this.currentItem.canUpdate;
  }

  get formCrls() {
    return this.form.controls;
  }

  onDplicate(resourceId: string) {
    this.dialog
      .open(DialogDuplicateCodeComponent, { data: { code: this.currentItem.code, maxLength: 10, duplicate: (code: string) => this.service.duplicate(resourceId, code) } })
      .afterClosed()
      .subscribe((res) => {
        if (res !== undefined) this.majStore(SnackbarTypes.duplicate);
      });
  }

  clickSave(currentItem: Atelier) {
    this.formSubmitted = true;
    if (this.form.invalid) {
      if (this.formCrls.projectType.invalid) this.scrollToBottom();
      return;
    }

    currentItem = Object.assign(currentItem, this.form.value);
    currentItem.detailedResource = false;
    currentItem.ceu = this.mapFormToCeu(this.form.value);

    const isCreatingOrCanUpdate = !this.currentItem?.resourceId || this.currentItem.canUpdate;
    currentItem.versionId = this.currentItem?.ruade ? this.formCrls.versionId.value : undefined;
    currentItem.energyId = isCreatingOrCanUpdate ? this.formCrls.energyId.value : undefined;

    const isCreation = !this.currentItem?.resourceId;
    const snackbarType = isCreation ? SnackbarTypes.add : SnackbarTypes.update;

    this.onUpdateOrSaveAch(currentItem).subscribe(
      (data: Atelier) => {
        this.currentItem = data;
        this.majStore(snackbarType);

        const navState = {
          perimeterId: this.perimeterId,
          resourceId: data.resourceId,
          families: this.families,
          projectTypes: this.projectTypes
        };

        const hasFile = isCreation && this.fileToUploadOnSave && data.resourceId;
        const hasCalculFile = isCreation && this.calculFileToUploadOnSave && data.resourceId;

        if (!hasFile && !hasCalculFile) {
          this.router.navigate([SiteMapUrl.MenuEnginAtelier, 'engin', data.resourceId], { state: navState });
          return;
        }

        const uploadFile$ = hasFile
          ? new Observable<Atelier>((observer) => {
              const formData = new FormData();
              formData.append('file', this.fileToUploadOnSave!, `${data.resourceId}.${this.fileToUploadName?.split('.').pop()}`);
              this.uploadService.uploadProof(data.resourceId!, formData).subscribe(
                (event: any) => {
                  if (event.type === HttpEventType.Response) {
                    data.fileDescription = event?.body;
                    data.fileName = this.fileToUploadName;
                    this.fileToUploadOnSave = undefined;
                    this.fileToUploadName = undefined;
                    observer.next(data);
                    observer.complete();
                  }
                },
                (error) => {
                  observer.error(error);
                }
              );
            })
          : of(data);

        const uploadCalculFile$ = hasCalculFile
          ? new Observable<Atelier>((observer) => {
              const formData = new FormData();
              formData.append('file', this.calculFileToUploadOnSave!, `${data.resourceId}-FC.${this.calculFileToUploadName?.split('.').pop()}`);
              this.uploadService.uploadProof(data.resourceId!, formData).subscribe(
                (event: any) => {
                  if (event.type === HttpEventType.Response) {
                    data.fileCalculCeuDescription = event?.body;
                    data.fileCalculCeuName = this.calculFileToUploadName;
                    this.calculFileToUploadOnSave = undefined;
                    this.calculFileToUploadName = undefined;
                    observer.next(data);
                    observer.complete();
                  }
                },
                (error) => {
                  observer.error(error);
                }
              );
            })
          : of(data);

        uploadFile$.pipe(switchMap(() => uploadCalculFile$)).subscribe(
          (finalData: Atelier) => {
            this.service.update(finalData).subscribe(
              (updated: Atelier) => {
                this.currentItem = updated;
                this.majStore(SnackbarTypes.update);
                this.router.navigate([SiteMapUrl.MenuEnginAtelier, 'engin', updated.resourceId], { state: navState });
              },
              (error) => {
                this.snackbarService.open(SnackbarTypes.error);
              }
            );
          },
          (error) => {
            this.snackbarService.open(SnackbarTypes.error);
          }
        );
      },
      (error) => {
        this.snackbarService.openError(error);
      }
    );
  }

  mapFormToCeu(formValue: any): Ceu {
    // La V2 recalcule le CO2 du carburant d'un engin qui a une énergie, puis les totaux journaliers (en, co) à partir des parts,
    // sauf pour un engin du référentiel modifié, qui garde ceux envoyés. La V1 (France) les enregistre tels quels : quand elle
    // sera retirée, n'envoyer les totaux que pour un engin du référentiel (currentItem.ruade), et ce CO2 qu'en « autre énergie »
    // ou pour un engin du référentiel.
    return {
      en: formValue.ceu?.en ?? 0,
      co: formValue.ceu?.co ?? 0,
      ceuEngin: {
        ca: {
          energie: formValue.ceuCarburant?.ceuCarburantEn ?? 0,
          co2: formValue.ceuCarburant?.ceuCarburantCo ?? 0
        },
        tr: {
          energie: formValue.ceuTransfert?.ceuTransfertEn ?? 0,
          co2: formValue.ceuTransfert?.ceuTransfertCo ?? 0
        },
        fa: {
          energie: formValue.ceuFabrication?.ceuFabricationEn ?? 0,
          co2: formValue.ceuFabrication?.ceuFabricationCo ?? 0
        },
        en: {
          energie: formValue.ceuEntretien?.ceuEntretienEn ?? 0,
          co2: formValue.ceuEntretien?.ceuEntretienCo ?? 0
        }
      }
    };
  }

  onUpdateOrSaveAch(currentItem: Atelier) {
    return this.currentItem?.resourceId ? this.service.update(currentItem) : this.service.add(currentItem);
  }

  majStore(snackbarMsgType: string) {
    this.store.select(StoreSelect.RESOURCES).subscribe((data) => {
      this.store.dispatch(new UpdateResourceAction(data));
    });

    this.service.getAll(GroupBy.family).subscribe(
      (arr) => {
        if (arr) {
          this.store.dispatch(new UpdateResourceAction(arr));
          this.snackbarService.open(snackbarMsgType);
        }
      },
      (error) => {
        this.snackbarService.open(SnackbarTypes.error);
      }
    );

    this.resourceListRefreshService.triggerRefresh();
  }

  onDeleteDialog(resourceId: string): void {
    const dialogRef = this.dialog.open(DialogOverviewComponent, {
      data: { title: this.translate.instant('confirm_delete_engin') }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.deleteEngin(resourceId);
      }
    });
  }

  deleteEngin(resourceId: string) {
    if (resourceId) {
      this.service.delete(resourceId).subscribe({
        next: () => {
          this.majStore(SnackbarTypes.delete);
          this.router.navigate([SiteMapUrl.MenuEnginAtelier]);
        },
        error: (error) => {
          this.snackbarService.open(SnackbarTypes.error);
          console.error(error);
        }
      });
    }
  }

  calculCeuHeure(val: any) {
    let dailyUsageTime = this.currentItem.dailyUsageTime;
    if (!dailyUsageTime) dailyUsageTime = 7;
    if (val) return val / dailyUsageTime;
    return 0;
  }

  uploadProof(resourceId?: string, files?: any) {
    if (!files || files.length === 0) return;

    const fileToUpload = <File>files[0];
    this.fileToUploadName = fileToUpload.name;

    if (!resourceId) {
      this.fileToUploadOnSave = fileToUpload;
      this.currentItem.fileName = fileToUpload.name;
      return;
    }

    const formData = new FormData();
    formData.append('file', fileToUpload, `${resourceId}.${fileToUpload.name.split('.').pop()}`);

    this.spinnerService.show();
    this.uploadService.uploadProof(resourceId, formData).subscribe(
      (event: any) => {
        if (event.type === HttpEventType.Response) {
          this.spinnerService.hide();
          this.currentItem.fileName = fileToUpload.name;
          this.currentItem.fileDescription = event?.body;
        }
      },
      () => {
        this.snackbarService.open(SnackbarTypes.error);
        this.spinnerService.hide();
      }
    );
  }

  deleteFile(currentItem: Atelier) {
    currentItem.fileName = undefined;
    currentItem.fileDescription = undefined;
    this.fileToUploadName = undefined;
    this.fileToUploadOnSave = undefined;
  }

  uploadCalcul(resourceId?: string, files?: any) {
    if (!files || files.length === 0) return;

    const calculFileToUpload = <File>files[0];
    this.calculFileToUploadName = calculFileToUpload.name;

    if (!resourceId) {
      this.calculFileToUploadOnSave = calculFileToUpload;
      this.currentItem.fileCalculCeuName = calculFileToUpload.name;
      return;
    }

    const formData = new FormData();
    formData.append('file', calculFileToUpload, `${resourceId}-FC.${calculFileToUpload.name.split('.').pop()}`);

    this.spinnerService.show();
    this.uploadService.uploadProof(resourceId, formData).subscribe(
      (event: any) => {
        if (event.type === HttpEventType.Response) {
          this.spinnerService.hide();
          this.currentItem.fileCalculCeuName = calculFileToUpload.name;
          this.currentItem.fileCalculCeuDescription = event?.body;
        }
      },
      () => {
        this.snackbarService.open(SnackbarTypes.error);
        this.spinnerService.hide();
      }
    );
  }

  deleteFileCalcul(currentItem: Atelier) {
    currentItem.fileCalculCeuName = undefined;
    currentItem.fileCalculCeuDescription = undefined;
    this.calculFileToUploadName = undefined;
    this.calculFileToUploadOnSave = undefined;
  }

  addToFavorite(resourceId: string) {
    this.utilsService.addToFavorite(resourceId).then((res) => {
      this.favoriteId = res;
    });
  }

  removeFromFavorite() {
    if (this.favoriteId) {
      this.utilsService.removeFromFavorite(this.favoriteId).then(() => {
        this.favoriteId = '';
      });
    }
  }

  private scrollToBottom(): void {
    if (this.resourceSection?.nativeElement) {
      this.resourceSection.nativeElement.scrollIntoView({
        behavior: 'smooth'
      });
    }
  }

  compareProjectTypes(pt1: ProjectType, pt2: ProjectType) {
    return compare(pt1, pt2);
  }

  viewDownloadCalculCeuEngins() {
    const dialogRef = this.dialog.open(DialogDownloadCalculCeuEnginsComponent);
  }
}
